import { MouseEventHandler, ReactElement } from 'react'
import { ButtonBase, styled, SxProps, Theme, Tooltip, TooltipProps } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { Typography } from '../../text'
import { Link as RouterLink } from 'react-router-dom'
import { IconContext } from '@phosphor-icons/react'

type TinyButtonContentProps = {
    label?: string
    leftIcon?: ReactElement
    rightIcon?: ReactElement
    disabled?: boolean
    tooltip?: string
    tooltipPlacement?: TooltipProps['placement']
}

export type TinyButtonProps = {
    onClick: MouseEventHandler<HTMLButtonElement>
    sx?: SxProps<Theme>
    ariaLabel?: string
} & TinyButtonContentProps

type TinyNavButtonProps = {
    href: string
    ariaLabel?: string
    target?: string
    rel?: string
} & TinyButtonContentProps

function TinyButtonContent({ label, leftIcon, rightIcon, disabled }: TinyButtonContentProps) {
    return (
        <>
            {leftIcon && (
                <IconContext.Provider
                    value={{
                        size: tokens.SizingIconS,
                        weight: 'bold',
                        color: disabled ? tokens.ColorIconDisabled : tokens.ColorIconBoldest,
                    }}
                >
                    {leftIcon}
                </IconContext.Provider>
            )}
            {label && (
                <Typography variant="link" noWrap>
                    {label}
                </Typography>
            )}
            {rightIcon && (
                <IconContext.Provider
                    value={{
                        size: tokens.SizingIconS,
                        weight: 'bold',
                        color: disabled ? tokens.ColorIconDisabled : tokens.ColorIconSubtle,
                    }}
                >
                    {rightIcon}
                </IconContext.Provider>
            )}
        </>
    )
}

export function TinyButton({
    label,
    leftIcon,
    rightIcon,
    onClick,
    disabled,
    tooltip,
    tooltipPlacement = 'top',
    sx,
    ariaLabel,
}: TinyButtonProps) {
    return (
        <Tooltip title={tooltip} placement={tooltipPlacement} sx={{ zIndex: 2000 }}>
            <span>
                <StyledButtonBase aria-label={ariaLabel} onClick={onClick} disableRipple disabled={disabled} sx={sx}>
                    <TinyButtonContent label={label} leftIcon={leftIcon} rightIcon={rightIcon} disabled={disabled} />
                </StyledButtonBase>
            </span>
        </Tooltip>
    )
}

export function TinyNavButton({ label, leftIcon, rightIcon, href, disabled, ariaLabel, target, rel }: TinyNavButtonProps) {
    return (
        <StyledButtonBase
            aria-label={ariaLabel}
            disableRipple
            disabled={disabled}
            component={RouterLink}
            to={href}
            target={target}
            rel={rel}
        >
            <TinyButtonContent label={label} leftIcon={leftIcon} rightIcon={rightIcon} disabled={disabled} />
        </StyledButtonBase>
    )
}

const StyledButtonBase = styled(ButtonBase)(() => ({
    width: 'fit-content',
    lineHeight: 0,
    height: '32px',
    borderRadius: tokens.SizingCornerS,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    gap: tokens.SizingSpacing100,
    color: tokens.ColorTextDefault,
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
    '&.Mui-disabled': {
        color: tokens.ColorTextDisabled,
        backgroundColor: tokens.ColorSurfaceDisabled,
    },
})) as typeof ButtonBase
